import React, { ReactElement, useEffect, useRef, useState } from "react";
import "./ThemeMenu.css";

export type SelectOption<T extends string> = {
  value: T;
  label: string;
  /** A small line under the label in the list. */
  hint?: string;
  /** A CSS background (colour or gradient) for a round sample dot before the label. */
  swatch?: string;
};

type SelectMenuProps<T extends string> = {
  /** "chip": a small button for the game's button row; "field": a wide button for the lobby. */
  variant: "chip" | "field";
  /** The word on the chip, and the accessible name of the list ("Theme", "Layout"). */
  title: string;
  options: SelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
};

/**
 * One drop-down for every personal setting (theme, layout, ...). It lists whatever it is given, so a new
 * choice is one more entry in the options of the caller.
 */
export default function SelectMenu<T extends string>(props: SelectMenuProps<T>): ReactElement {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const current = props.options.find((o) => o.value === props.value) ?? props.options[0];

  // Close on a click elsewhere or on Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (value: T) => {
    props.onChange(value);
    setOpen(false);
  };

  return (
    <div className={"tm-root tm-" + props.variant} ref={root}>
      <button
        className={props.variant === "chip" ? "game-chip tm-button" : "lb-pill tm-button tm-field-button"}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {current.swatch && <span className="tm-dot" style={{ background: current.swatch }} aria-hidden="true" />}
        {props.variant === "chip" ? props.title.toUpperCase() : current.label}
        <span className="tm-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <ul className="tm-list" role="listbox" aria-label={props.title}>
          {props.options.map((o) => (
            <li key={o.value} role="none">
              <button
                role="option"
                aria-selected={o.value === props.value}
                className={"tm-option" + (o.value === props.value ? " on" : "")}
                onClick={() => choose(o.value)}
              >
                {o.swatch && <span className="tm-dot" style={{ background: o.swatch }} aria-hidden="true" />}
                <span className="tm-text">
                  {o.label}
                  {o.hint && <span className="tm-hint">{o.hint}</span>}
                </span>
                {o.value === props.value && (
                  <span className="tm-check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
